import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Tabs, Tab,
  List, ListItemButton, ListItemText, Chip, Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';

export default function LoginPage({ onLogin }) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ email: '', password: '' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [roles, setRoles] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await apiClient.post('/auth/login', form);
      if (data.needs_context) {
        setPendingToken(data.token);
        setPendingUser(data.user);
        setRoles(data.roles);
      } else {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(form.email);
      } else {
        setError(err.response?.data?.error || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const requestInvite = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/auth/request-invite', { email: inviteEmail });
      setInviteSent(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setRegistrationClosed(true);
      } else {
        setError(err.response?.data?.error || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const assume = async (org_user_id) => {
    setLoading(true);
    try {
      const { data } = await apiClient.post('/auth/assume', { org_user_id }, {
        headers: { Authorization: `Bearer ${pendingToken}` },
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (unverifiedEmail) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <Card sx={{ width: 400 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} mb={2}>Email not verified</Typography>
            <Typography color="text.secondary" mb={3}>
              Please verify your email address before signing in.
            </Typography>
            <Button component={Link} to="/resend-verification" variant="contained" fullWidth sx={{ mb: 1 }}>
              Resend verification email
            </Button>
            <Button onClick={() => setUnverifiedEmail(null)} fullWidth>Back to sign in</Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (roles) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <Card sx={{ width: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} mb={1}>Select a Brokerage</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              You have multiple roles. Choose which context to work in.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <List disablePadding>
              {roles.map((r, i) => (
                <React.Fragment key={r.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton onClick={() => assume(r.id)} disabled={loading}>
                    <ListItemText
                      primary={r.org_name}
                      secondary={`Role: ${r.role}${r.is_personal ? ' (personal)' : ''}`}
                    />
                    <Chip label={r.role} size="small" />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
            {pendingUser?.is_system_admin && (
              <>
                <Divider sx={{ my: 2 }} />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    localStorage.setItem('token', pendingToken);
                    localStorage.setItem('user', JSON.stringify(pendingUser));
                    onLogin(pendingUser);
                  }}
                >
                  Continue as System Admin (no brokerage)
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
      <Card sx={{ width: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={3}>Showing Planner</Typography>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="Sign In" />
            <Tab label="Register" />
          </Tabs>
          {tab === 0 && (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Email" type="email" value={form.email} onChange={set('email')} required size="small" />
                <TextField label="Password" type="password" value={form.password} onChange={set('password')} required size="small" />
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1 }}>Sign In</Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                <Link to="/forgot-password" style={{ color: '#1976d2' }}>Forgot password?</Link>
              </Typography>
            </>
          )}
          {tab === 1 && (
            registrationClosed ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Registration is currently by invitation only. Contact an administrator to get access.
              </Alert>
            ) : inviteSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                If that email isn't already registered, a registration link has been sent. Check your inbox.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Enter your email and we'll send you a link to create your account.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box component="form" onSubmit={requestInvite} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Email" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required size="small" autoFocus />
                  <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1 }}>
                    {loading ? 'Sending…' : 'Send registration link'}
                  </Button>
                </Box>
              </>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
